// Nuxt Content's generated types expect literal collection names (a union
// baked from content.config.ts at build time). Our collection names are
// computed at runtime via slugToCollection(site), so the generated types
// reject them. These helpers confine the unsafe cast to a single place and
// give call sites a clean, loosely-typed API.
//
// **Why these are async + dynamic-import**: previously this file relied on
// Nuxt's auto-imports for `queryCollection`, `queryCollectionNavigation`,
// and `queryCollectionSearchSections`. Auto-import inserts a STATIC import
// of `@nuxt/content/dist/runtime/client` (and its transitive deps —
// `internal/query.js`, `navigation.js`, `surround.js`, `search.js`,
// `api.js`). Rollup gathered that into a ~1 MiB shared chunk that the
// browser eagerly fetched on every cold load, even when every
// `useAsyncData` call was payload-cached and never re-ran client-side.
//
// Wrapping in a dynamic import gates the chunk behind the *first* client
// query. Most landings render entirely from SSR'd payload data and never
// pay the chunk cost. SPA navigation, search-on-open, and any other
// genuinely-client query pays a one-shot ~150 ms load, then everything
// hits the JS-module cache.
//
// Server path: dynamic import resolves immediately (no extra latency).

type ContentQuery = {
  select(...fields: string[]): ContentQuery;
  where(field: string, op: string, value: unknown): ContentQuery;
  andWhere(cb: (q: ContentQuery) => ContentQuery): ContentQuery;
  orWhere(cb: (q: ContentQuery) => ContentQuery): ContentQuery;
  order(field: string, direction?: "ASC" | "DESC"): ContentQuery;
  limit(n: number): ContentQuery;
  skip(n: number): ContentQuery;
  path(p: string): ContentQuery;
  first(): Promise<any>;
  all(): Promise<any[]>;
  count(): Promise<number>;
};

// Cache the module reference so we resolve the dynamic import exactly once
// per process. On the server this is per request-handler instance; on the
// client this is per page session. We dynamic-import a thin local
// wrapper (`./_contentClient.ts`) rather than `@nuxt/content/dist/runtime/client`
// directly because the package's `exports` map doesn't expose that subpath
// and Vite's CommonJS resolver refuses to look around it. See
// `./_contentClient.ts` for the reasoning.
let _clientModule: typeof import("./_contentClient") | null = null;

async function _loadClient() {
  if (!_clientModule) {
    _clientModule = await import("./_contentClient");
  }
  return _clientModule;
}

export async function queryPages(col: string): Promise<ContentQuery> {
  const m = await _loadClient();
  return (m._queryCollection as unknown as (c: string) => ContentQuery)(col);
}

export async function queryNav(col: string, extraFields: string[] = []): Promise<any[]> {
  const m = await _loadClient();
  return (m._queryCollectionNavigation as unknown as (
    c: string,
    f: string[],
  ) => Promise<any[]>)(col, extraFields);
}

// `stemLike` restricts the parsed page set with a `stem LIKE <pattern>` filter
// BEFORE `generateSearchSections` reads + AST-parses each body. The docs site
// collections span every locale (the include glob is `*/{docs,...}/…`), so
// without this the client parses all ~7 locales' markdown on first open just to
// keep one. Chaining `.where` onto the chainable query (see @nuxt/content's
// `chainablePromise`) ANDs with its own `extension = md` filter, so only the
// target-locale pages are fetched and parsed — a ~7× cut in load work.
export async function querySearchSections(
  col: string,
  opts?: { extraFields?: string[]; stemLike?: string },
): Promise<any[]> {
  const m = await _loadClient();
  const { stemLike, ...searchOpts } = opts ?? {};
  type Chainable = Promise<any[]> & {
    where(field: string, op: string, value: unknown): Chainable;
  };
  let q = (m._queryCollectionSearchSections as unknown as (
    c: string,
    o?: { extraFields?: string[] },
  ) => Chainable)(col, searchOpts);
  if (stemLike) q = q.where("stem", "LIKE", stemLike);
  return q;
}
